import React from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useQueryStates, parseAsInteger, parseAsBoolean } from 'nuqs'
import { useChargesQuery } from 'queries/admin/charges'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'

type ChargesContextState = {
  state: {
    data: ReturnType<typeof useChargesQuery>['data']
    dataUpdatedAt: number
    isPending: boolean
    isFetching: boolean
    isError: boolean
    error: string
    hasIdentityFilter: boolean
    companyId: number | null
    shiftId: number | null
    workerId: number | null
    invoiceId: number | null
    invoiced: boolean | null
    pagination: {
      pageIndex: number
      pageSize: number
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setCompanyId: (value: number | undefined) => void
    setShiftId: (value: number | undefined) => void
    setWorkerId: (value: number | undefined) => void
    setInvoiceId: (value: number | undefined) => void
    setInvoiced: (value: boolean | null) => void
  }
}

const [Context, useChargesContextValue] = createContext<ChargesContextState>({
  name: 'ChargesContext',
})

export const ChargesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [pagination, setPagination] = usePaginationQueryState({
    defaultPageSize: 5,
  })
  const [filters, setFilters] = useQueryStates(
    {
      companyId: parseAsInteger,
      shiftId: parseAsInteger,
      workerId: parseAsInteger,
      invoiceId: parseAsInteger,
      invoiced: parseAsBoolean,
    },
    {
      urlKeys: {
        companyId: 'company',
        shiftId: 'shift',
        workerId: 'worker',
        invoiceId: 'invoice',
        invoiced: 'invoiced',
      },
    }
  )

  const hasIdentityFilter =
    filters.companyId != null ||
    filters.shiftId != null ||
    filters.workerId != null ||
    filters.invoiceId != null

  const resetPage = () => {
    setPagination({ pageIndex: 0 })
  }

  const setCompanyId = (value: number | undefined) => {
    setFilters({ companyId: value ?? null })
    resetPage()
  }

  const setShiftId = (value: number | undefined) => {
    setFilters({ shiftId: value ?? null })
    resetPage()
  }

  const setWorkerId = (value: number | undefined) => {
    setFilters({ workerId: value ?? null })
    resetPage()
  }

  const setInvoiceId = (value: number | undefined) => {
    setFilters({ invoiceId: value ?? null })
    resetPage()
  }

  const setInvoiced = (value: boolean | null) => {
    setFilters({ invoiced: value })
    resetPage()
  }

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useChargesQuery(
    {
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      companyId: filters.companyId ?? undefined,
      shiftId: filters.shiftId ?? undefined,
      workerId: filters.workerId ?? undefined,
      invoiceId: filters.invoiceId ?? undefined,
      invoiced: filters.invoiced ?? undefined,
    },
    { enabled: hasIdentityFilter }
  )

  const value: ChargesContextState = {
    state: {
      data,
      dataUpdatedAt,
      isPending,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      hasIdentityFilter,
      companyId: filters.companyId,
      shiftId: filters.shiftId,
      workerId: filters.workerId,
      invoiceId: filters.invoiceId,
      invoiced: filters.invoiced,
      pagination,
    },
    actions: {
      refetch,
      setPagination,
      setCompanyId,
      setShiftId,
      setWorkerId,
      setInvoiceId,
      setInvoiced,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useChargesContextValue }
